import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertTriangle, Trash2, Loader2 } from 'lucide-react';

const DeleteLeadDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  lead,
  isDeleting 
}) => {
  const [step, setStep] = useState(1);
  const [confirmationText, setConfirmationText] = useState('');
  
  const expectedText = lead 
    ? `SUPPRIMER ${lead.company || lead.name || 'Lead'}` 
    : '';
  
  const isConfirmationValid = confirmationText.trim() === expectedText;

  const handleClose = () => {
    setStep(1);
    setConfirmationText('');
    onClose();
  };

  const handleFirstConfirm = (e) => {
    e.preventDefault();
    setStep(2);
  };

  const handleFinalConfirm = () => {
    if (isConfirmationValid) {
      onConfirm(confirmationText);
    }
  };

  if (!lead) return null;

  return (
    <AlertDialog open={isOpen} onOpenChange={handleClose}>
      <AlertDialogContent className="max-w-md">
        {step === 1 ? (
          <>
            <AlertDialogHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-red-100">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <AlertDialogTitle className="text-xl">
                  Supprimer ce lead ?
                </AlertDialogTitle>
              </div>
              <AlertDialogDescription className="text-left mt-4 space-y-3" asChild>
                <div>
                  <p>
                    Vous êtes sur le point de supprimer définitivement le lead :
                  </p>
                  <div className="p-3 bg-gray-50 rounded-lg border">
                    <p className="font-semibold text-gray-900">
                      {lead.name}
                    </p>
                    <p className="text-sm text-gray-500">{lead.company}</p>
                    <p className="text-sm text-gray-500">{lead.email}</p>
                  </div>
                  <p className="text-red-600 font-medium">
                    Cette action est irréversible. Toutes les données associées seront supprimées.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-6">
              <AlertDialogCancel onClick={handleClose}>
                Annuler
              </AlertDialogCancel>
              <Button
                onClick={handleFirstConfirm}
                className="bg-red-600 hover:bg-red-700"
                data-testid="delete-lead-continue-btn"
              >
                Continuer
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-red-100">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <AlertDialogTitle className="text-xl">
                  Confirmation finale
                </AlertDialogTitle>
              </div>
              <AlertDialogDescription className="text-left mt-4 space-y-4" asChild>
                <div>
                  <p>
                    Pour confirmer la suppression, tapez exactement :
                  </p>
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <code className="text-red-700 font-mono font-semibold">
                      {expectedText}
                    </code>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lead-confirmation">Confirmation</Label>
                    <Input
                      id="lead-confirmation"
                      value={confirmationText}
                      onChange={(e) => setConfirmationText(e.target.value)}
                      placeholder="Tapez le texte ci-dessus..."
                      className={`font-mono ${
                        confirmationText && !isConfirmationValid 
                          ? 'border-red-500 focus:ring-red-500' 
                          : ''
                      }`}
                      autoComplete="off"
                      data-testid="delete-lead-confirmation-input"
                    />
                    {confirmationText && !isConfirmationValid && (
                      <p className="text-sm text-red-500">
                        Le texte ne correspond pas
                      </p>
                    )}
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setStep(1)}>
                Retour
              </Button>
              <Button
                onClick={handleFinalConfirm}
                disabled={!isConfirmationValid || isDeleting}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
                data-testid="confirm-delete-lead-button"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer définitivement
                  </>
                )}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteLeadDialog;
